"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.displayStealthMode = exports.insertWebAnalytics = exports.insertLogTrigger = exports.debug = exports.init = void 0;

/** 
 * Logic that supports access logging and web analytics in a web page.
 */

/**
 * URL of the <img>-compliant resource that triggers access logging, sans querystring. For example: 
 *  https://example.com/cdn/log.png
 */
var logTriggerUrl;
/**
 * DOM querySelector-compliant selector string that identifies the DOM element to which the 
 * logTriggerUrl-referencing <img> will be appended. Defaults to '#log-trigger-box'.
 */

var logTriggerSelector;
/**
 * Token string that will be passed to Cloudflare web analytics as the value of the 'token' 
 * property of the 'data-cf-beacon' <script> attribute. If not supplied, web analytics will not be 
 * enabled.
 */

var cloudflareToken;
/**
 * DOM querySelector-compliant selector string that identifies the DOM element that will have a 
 * 'stealth-mode' class assigned to it when stealth mode is activated. Defaults to '.site-header'.
 */

var stealthModeSelector;
/**
 * Array of strings identifying hostnames which will always trigger stealth mode when one is found 
 * to match the current window.location.hostname. Defaults to [ 'localhost', '10.0.2.2' ]. 
 * 10.0.2.2 is the IP address used by VirtualBox to access the host system.
 */

var stealthModeHostnamesArr;
/**
 * If true, the access log request will be made using CORS restrictions. This may be desired to 
 * prevent access logging from being triggered from a non-production environment, or to prevent 
 * mismatches between the website environment and logging context.
 */

var forceCors = false;
/**
 * Initialize the module with user-supplied options.
 */

var init = function init(options) {
  logTriggerUrl = options.logTriggerUrl;
  logTriggerSelector = options.logTriggerSelector || '#log-trigger-box';
  cloudflareToken = options.cloudflareToken;
  stealthModeSelector = options.stealthModeSelector || '.site-header';
  stealthModeHostnamesArr = options.stealthModeHostnamesArr || ['localhost', '10.0.2.2'];
  forceCors = options.forceCors || false;
};
/**
 * Log to the console the values of all variables that the user has the option to set when 
 * initializing the module.
 */


exports.init = init;

var debug = function debug() {
  console.log('logTriggerUrl = %o', logTriggerUrl);
  console.log('logTriggerSelector = %o', logTriggerSelector);
  console.log('cloudflareToken = %o', cloudflareToken);
  console.log('stealthModeSelector = %o', stealthModeSelector);
  console.log('stealthModeHostnamesArr = %o', stealthModeHostnamesArr);
  console.log('forceCors = %o', forceCors);
};
/**
 * Insert into the DOM an element that will trigger a request that will serve to log the context 
 * user's page request. The requested path, referrer (if available), and data about the response 
 * are encoded in the log request.
 * Originally sourced from https://benhoyt.com/writings/replacing-google-analytics/#cloudfront-pixel
 */


exports.debug = debug;

var insertLogTrigger = function insertLogTrigger() {
  if (!calcShouldBypassLogging()) {
    var imgEle = document.createElement('img');
    /*
     * Could also use build-time page properties instead of request-time client-side 
     * JavaScript, but this method allows us to capture requests that result in a 404 response.
     */

    var pageId = window.location.pathname; // If there is no referrer, document.referrer will equal "".

    var referrer = document.referrer;
    var statusCode = calcStatusCodeToLog();
    var protocolVersion = calcProtocolVersionToLog();
    var byteCnt = calcResponseByteCountToLog(); //console.log('protocolVersion = %o', protocolVersion);
    //console.log('byteCnt = %o', byteCnt);

    imgEle.src = logTriggerUrl + '?u=' + encodeURIComponent(pageId) + '&s=' + encodeURIComponent(statusCode) + '&p=' + encodeURIComponent(protocolVersion) + '&b=' + encodeURIComponent(byteCnt) + '&r=' + encodeURIComponent(referrer);
    /*
     * If specified, force the log trigger request to be made using CORS restrictions. More 
     * info: https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/crossOrigin
     */

    if (forceCors) {
      imgEle.crossOrigin = "anonymous";
    }

    var containingEle = document.querySelector(logTriggerSelector);

    if (containingEle === null) {
      containingEle = document.body;
    }

    containingEle.appendChild(imgEle);
  }
};
/**
 * Insert script(s) required for feeding each page request into third-party web analytics.
 *
 * Note that the hostname entered when configuring Cloudflare web analytics must match the website 
 * the JavaScript is loaded from. If the hostname and website do not match, a CORS (Cross Origin 
 * Resource-Sharing) error will be raised in the browser. The error message will say that the 
 * origin "is not allowed by Access-Control-Allow-Origin."
 */


exports.insertLogTrigger = insertLogTrigger;

var insertWebAnalytics = function insertWebAnalytics() {
  if (!calcShouldBypassLogging() && cloudflareToken !== '' && typeof cloudflareToken !== 'undefined') {
    var scriptEle = document.createElement('script');
    scriptEle.type = 'text/javascript';
    scriptEle.src = 'https://static.cloudflareinsights.com/beacon.min.js';
    scriptEle.onerror = handleScriptLoadError; // Force the scripts to be executed in document order, as when using 'defer'.

    scriptEle.setAttribute('async', false);
    scriptEle.setAttribute('data-cf-beacon', '{"token": "' + cloudflareToken + '"}'); //console.log('appending %s', scriptEle.src);

    /*
     * Each script will start loading as soon as it is appended to the document. More info: 
     *  https://javascript.info/script-async-defer#dynamic-scripts
     */

    document.body.appendChild(scriptEle);
  }
};
/**
 * Mutate the UI in some way to provide clear indication when in stealth mode.
 */


exports.insertWebAnalytics = insertWebAnalytics;

var displayStealthMode = function displayStealthMode() {
  var stealthMode = establishStealthMode();
  var siteHeaderEle = document.querySelector(stealthModeSelector);

  if (siteHeaderEle !== null && siteHeaderEle.classList) {
    if (stealthMode) {
      siteHeaderEle.classList.add('stealth-mode');
      console.log('stealthMode = %o', stealthMode);
    } else {
      siteHeaderEle.classList.remove('stealth-mode');
    }
  } else if (stealthMode) {
    // Fallback for unsupported browsers.
    console.log('stealthMode = %o', stealthMode);
  }
};
/**
 * Return true if logging and analytics should be bypassed, false otherwise. Originally added 
 * to prevent test and development activities from skewing usage statistics.
 */


exports.displayStealthMode = displayStealthMode;

var calcShouldBypassLogging = function calcShouldBypassLogging() {
  var result = false;
  var stealthMode = establishStealthMode();
  var hostNamesToIgnore = stealthModeHostnamesArr;
  var hostName = window.location.hostname;

  if (stealthMode || hostNamesToIgnore.indexOf(hostName) > -1) {
    result = true;
  }

  return result;
};
/**
 * Return true if the app is running in stealth mode, false otherwise. If stealth mode is being 
 * triggered, persist it in session state. If stealth mode is being terminated, remove it from 
 * session state.
 *
 * When the app is running in stealth mode, logging and analytics are disabled. Originally added 
 * to prevent test activities from skewing usage statistics.
 *
 * Stealth mode may be activated by placing "stealth=true" in the querystring of the context 
 * browser's URL, and may be deactivated with "stealth=false".
 */


var establishStealthMode = function establishStealthMode() {
  var stealthMode = null;
  /*
   * Provide for an on-demand mechanism to enable/disable stealth mode. Stealth mode can also 
   * be disabled by clearing session storage via the browser's developer tools.
   */

  var stealthKey = 'stealth=';
  var idx = window.location.search.indexOf(stealthKey);

  if (idx > -1) {
    idx += stealthKey.length;
    var stealthVal = window.location.search.substring(idx, idx + 4);

    if (stealthVal === 'true') {
      stealthMode = true;
    } else if (stealthVal === 'fals') {
      stealthMode = false;
    }
  }
  /*
   * For convenience, persist the stealth mode state, when triggered, in session storage. 
   * Otherwise, retrieve the stealth mode state from session storage.
   */


  var storage = window.sessionStorage;

  if (stealthMode === true) {
    storage.setItem('stealthMode', stealthMode.toString());
  } else if (stealthMode === false) {
    storage.removeItem('stealthMode');
  } else {
    var storedStealthMode = storage.getItem('stealthMode');

    if (storedStealthMode === 'true') {
      stealthMode = true;
    }
  }

  return stealthMode;
};
/**
 * Determine the status code of the response to be included in the access log record.
 */


var calcStatusCodeToLog = function calcStatusCodeToLog() {
  var statusCode = '200';
  var bodyEle = document.querySelector('body');
  var pageSlug = bodyEle.getAttribute('data-page-slug'); // Use the page/file slug to determine if a 404 is being returned to the user.

  if (pageSlug === '404') {
    statusCode = '404';
  }

  return statusCode;
};
/**
 * Determine the HTTP protocol version of the response to be included in the access log record.
 */


var calcProtocolVersionToLog = function calcProtocolVersionToLog() {
  var protocolVersion = '';

  try {
    if (performance && performance.getEntries) {
      // Currently, this only returns a useful value in Chrome.
      protocolVersion = performance.getEntries()[0].nextHopProtocol;

      if (typeof protocolVersion !== 'undefined') {
        protocolVersion = protocolVersion.toUpperCase();
      } else {
        protocolVersion = '';
      }
    }
  } catch (error) {
    console.error(error);
  }

  return protocolVersion;
};
/**
 * Determine the byte count of the response to be included in the access log record.
 */


var calcResponseByteCountToLog = function calcResponseByteCountToLog() {
  var byteCnt = '';

  try {
    if (performance && performance.getEntries) {
      // Currently, this only returns a useful value in Chrome.
      byteCnt = performance.getEntries()[0].encodedBodySize;

      if (byteCnt === 0 || byteCnt === '0' || typeof byteCnt === 'undefined') {
        byteCnt = '';
      }
    }
  } catch (error) {
    console.error(error);
  }

  return byteCnt;
};
/**
 * Handle an error raised while loading a script file.
 */


var handleScriptLoadError = function handleScriptLoadError(errorEvent) {
  console.error('errorEvent = %o', errorEvent);
  throw new URIError('Unable to load script ' + errorEvent.target.src);
};
